import clsx from 'clsx';
import { ConfigurationJalon, StatutZone } from '../../types';

/**
 * Schema visuel du poste (SFD jalonnement Contact/Large, #116). Trace SVG et
 * couleurs repris a l'identique de la maquette de reference (canevas "Falcon
 * Deneigement - Jalonnement Contact/Large", artboards Main.dc.html pour
 * CONTACT_OCCUPE, Agent_Large.dc.html pour LARGE_OCCUPE, Agent_NonOccupe.dc.html
 * pour CONTACT_LIBRE). LARGE_LIBRE n'a pas de maquette dediee : le contour
 * rempli de CONTACT_LIBRE est reutilise avec le repere "BUS" de LARGE_OCCUPE,
 * par extrapolation raisonnable documentee ici et dans
 * `Zone::getJalonsReference()` cote backend.
 */

export interface PosteSchemaPin {
  numero: number;
  statut: StatutZone;
  pinX: number;
  pinY: number;
}

interface PosteSchemaProps {
  configuration: ConfigurationJalon;
  pins: PosteSchemaPin[];
  onPinClick?: (numero: number) => void;
  className?: string;
}

const WIDTH = 300;
const HEIGHT = 280;

const PIN_STYLES: Record<StatutZone, { bg: string; outline?: string }> = {
  termine: { bg: '#16A34A' },
  en_cours: { bg: '#2563EB', outline: '#BFDBFE' },
  a_faire: { bg: '#9CA3AF' },
};

/** Repere "passerelle" (gangway) utilise par les deux variantes CONTACT. */
function GangwayMarker() {
  return (
    <>
      <path d="M60 26v40M80 26v40M48 62h46v12H48z" stroke="#4B5563" strokeWidth={8} strokeLinejoin="round" />
      <path d="M66 74l26 26M84 74l20 22" stroke="#4B5563" strokeWidth={7} strokeLinecap="round" />
    </>
  );
}

/** Repere "BUS" utilise par les deux variantes LARGE. */
function BusMarker() {
  return (
    <>
      <rect x={14} y={40} width={58} height={22} rx={3} fill="#4B5563" />
      <text x={43} y={55} fill="#fff" fontFamily="Inter,sans-serif" fontSize={11} fontWeight={700} textAnchor="middle">
        BUS
      </text>
      <path d="M56 104l-10 6 26 6z" fill="#6B7280" />
    </>
  );
}

/** Passerelle telescopique (jetway) au centre, visible uniquement en OCCUPE. */
function JetwayShape() {
  return (
    <>
      <rect x={143} y={58} width={14} height={150} rx={7} fill="#fff" stroke="#6B7280" />
      <path d="M143 120L84 160v8l59-22zM157 120l59 40v8l-59-22z" fill="#fff" stroke="#6B7280" />
      <path d="M145 190l-22 14v5l22-6zM155 190l22 14v5l-22-6z" fill="#fff" stroke="#6B7280" />
    </>
  );
}

function PosteBackground({ configuration }: { configuration: ConfigurationJalon }) {
  const estOccupe = configuration === 'contact_occupe' || configuration === 'large_occupe';
  const estLarge = configuration === 'large_occupe' || configuration === 'large_libre';

  return (
    <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} fill="none">
      <rect x={0} y={0} width={300} height={280} rx={8} fill="#E9EBEE" />
      <rect x={0} y={0} width={230} height={26} fill="#4B5563" />

      {estLarge ? <BusMarker /> : <GangwayMarker />}

      {estOccupe ? (
        <>
          <path d="M112 44h76v26l52 46v164M112 44v52l-56 32v152" stroke="#DC2626" strokeWidth={3} />
          <line x1={150} y1={210} x2={150} y2={280} stroke="#EAB308" strokeWidth={4} />
          <JetwayShape />
          <rect x={0} y={250} width={300} height={30} fill="#111827" opacity={0.85} />
        </>
      ) : (
        <>
          <path
            d="M112 44h76v26l52 46v164H56V128l56-32z"
            fill="#DBEAFE"
            fillOpacity={0.7}
            stroke="#2563EB"
            strokeWidth={3}
          />
          <line x1={150} y1={56} x2={150} y2={280} stroke="#EAB308" strokeWidth={4} />
        </>
      )}
    </svg>
  );
}

export function PosteSchema({ configuration, pins, onPinClick, className }: PosteSchemaProps) {
  const posteComplet = configuration === 'contact_libre' || configuration === 'large_libre';

  return (
    <div
      className={clsx(
        'bg-white border border-gray-200 rounded-2xl p-2.5 flex justify-center',
        className
      )}
    >
      <div style={{ position: 'relative', width: WIDTH, height: HEIGHT }}>
        <PosteBackground configuration={configuration} />
        {pins.map((pin) => {
          const style = PIN_STYLES[pin.statut];
          const taille = posteComplet ? 46 : 30;
          return (
            <button
              key={pin.numero}
              type="button"
              aria-label={`Jalon ${pin.numero}`}
              onClick={onPinClick ? () => onPinClick(pin.numero) : undefined}
              style={{
                position: 'absolute',
                left: pin.pinX * WIDTH,
                top: pin.pinY * HEIGHT,
                transform: 'translate(-50%, -50%)',
                width: taille,
                height: taille,
                borderRadius: posteComplet ? 9 : 7,
                background: style.bg,
                color: '#fff',
                fontWeight: 800,
                fontSize: posteComplet ? 22 : 15,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                outline: style.outline ? `3px solid ${style.outline}` : undefined,
                border: 'none',
                cursor: onPinClick ? 'pointer' : 'default',
              }}
            >
              {pin.numero}
            </button>
          );
        })}
      </div>
    </div>
  );
}
